import axios from "axios";
import { useState } from "react";
import { Check, Pencil, Plus, Trash2, X } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "./ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GroupManager({ groups, onCreated }) {
  const [kind, setKind] = useState("expense");
  const [name, setName] = useState("");
  const [notice, setNotice] = useState("");
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [pendingDelete, setPendingDelete] = useState(null);
  const items = (groups.items || []).filter(g => g.kind === kind);
  const income = kind === "income";
  const label = income ? "Gelir kalemi" : "Gider grubu";

  const call = async (fn, ok) => {
    try { await fn(); setNotice(ok); onCreated?.(); }
    catch (e) { setNotice(e?.response?.data?.detail || "İşlem başarısız"); }
  };

  const add = event => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    call(() => axios.post(`${API}/groups`, { name: trimmed, kind }), `${label} eklendi`).then(() => setName(""));
  };

  const rename = g => {
    const next = draft.trim();
    setEditing(null);
    if (!next || next === g.name) return;
    call(() => axios.put(`${API}/groups/${encodeURIComponent(g.name)}`, { kind, new_name: next }), `${label} yeniden adlandırıldı`);
  };

  const confirmDelete = () => {
    const g = pendingDelete;
    setPendingDelete(null);
    call(() => axios.delete(`${API}/groups/${encodeURIComponent(g.name)}?kind=${kind}`), `${label} silindi; kayıtlar "Diğer"e taşındı`);
  };

  return (
    <section className="panel group-manager">
      <div className="panel-head">
        <div>
          <p className="eyebrow">{income ? "GELİR KALEMLERİ" : "GİDER GRUPLARI"}</p>
          <h3>{income ? "Gelir kalemlerini yönet" : "Gider gruplarını yönet"}</h3>
        </div>
        <div className="segmented mini" data-testid="group-kind-toggle">
          <button type="button" data-testid="group-kind-expense" className={!income ? "selected expense-btn" : ""} onClick={() => { setKind("expense"); setEditing(null); }}>Gider</button>
          <button type="button" data-testid="group-kind-income" className={income ? "selected income-btn" : ""} onClick={() => { setKind("income"); setEditing(null); }}>Gelir</button>
        </div>
      </div>
      <form onSubmit={add} className="group-form">
        <input data-testid="new-group-input" placeholder={income ? "Ör: Reklam Geliri" : "Ör: Ofis Malzemeleri"} value={name} onChange={e => setName(e.target.value)}/>
        <button className="outline-btn" data-testid="add-group-button" type="submit"><Plus size={16}/> Ekle</button>
      </form>
      <div className="group-chips" data-testid={`group-chips-${kind}`}>
        {items.map(g => editing === g.name ? (
          <span key={g.name} className="group-tag editing" data-testid={`group-edit-${g.name}`}>
            <input autoFocus data-testid="group-rename-input" value={draft} onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); rename(g); } if (e.key === "Escape") setEditing(null); }}/>
            <button type="button" className="chip-btn" data-testid="group-rename-save" title="Kaydet" onClick={() => rename(g)}><Check size={12}/></button>
            <button type="button" className="chip-btn" data-testid="group-rename-cancel" title="Vazgeç" onClick={() => setEditing(null)}><X size={12}/></button>
          </span>
        ) : (
          <span key={g.name} className={`group-tag ${kind} ${g.is_default ? "default" : ""}`} data-testid={`group-chip-${g.name}`}>
            {g.name}
            {g.name !== "Diğer" && (
              <button type="button" className="chip-btn" title="Yeniden adlandır" data-testid={`group-rename-${g.name}`} onClick={() => { setEditing(g.name); setDraft(g.name); }}><Pencil size={11}/></button>
            )}
            {!g.is_default && (
              <button type="button" className="chip-btn danger" title="Sil" data-testid={`group-delete-${g.name}`} onClick={() => setPendingDelete(g)}><Trash2 size={11}/></button>
            )}
          </span>
        ))}
      </div>
      <small className="hint">Hazır gruplar yeniden adlandırılabilir, silinemez. Silinen grubun kayıtları "Diğer"e taşınır.</small>
      {notice && <small data-testid="group-notice" className="group-notice">{notice}</small>}

      <AlertDialog open={!!pendingDelete} onOpenChange={open => !open && setPendingDelete(null)}>
        <AlertDialogContent data-testid="group-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{label} silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              <b>{pendingDelete?.name}</b> silinecek; bu gruptaki tüm kayıtlar otomatik olarak "Diğer" altına taşınacak.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="group-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction data-testid="group-delete-confirm" className="danger-action" onClick={confirmDelete}>Evet, sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
